<template>
    <ion-page>
        <ion-loading :is-open="busy" message="Loading, please wait"/>
        <standard-header title="Find Item" align="middle"/>
        <ion-content>
            <ion-list>
                <ion-item lines="none">
                    <ion-label class="ion-text-wrap" v-if="hasScannedItem">
                        <h2>{{ scannedItem.name }}</h2>
                        <p>{{ scannedItem.item_number }}</p>
                    </ion-label>
                    <ion-label v-else>
                        <h2>Scan or search for item</h2>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="openItemScanner()">
                        <ion-icon slot="icon-only" :icon="barcode"/>
                    </ion-button>
                    <ion-button expand="block" color="primary" size="medium" @click="openItemPicker()">
                        <ion-icon slot="icon-only" :icon="search"/>
                    </ion-button>
                </ion-item>
                <div class="ion-padding-horizontal">
                    <ion-button v-if="hasScannedItem" expand="block" fill="outline" color="dark" @click="$refs['product'].open()">Product Card</ion-button>
                    <product-card-modal ref="product" :item="scannedItem" />
                </div>
            </ion-list>

            <ion-list v-if="hasScannedItem">
                <ion-item lines="none" button detail @click="customerReturn">
                    New Customer Return
                </ion-item>
            </ion-list>

            <ion-list v-if="hasScannedItem">
                <ion-list-header>Amazon Returns</ion-list-header>
                <ion-item button detail v-for="removal in amazonRemovals" :key="removal.id"
                          @click="open(removal)"
                >
                    <ion-avatar slot="start" class="ion-justify-content-center ion-align-items-center">{{ removal.qty_received }}</ion-avatar>
                    <ion-label>
                        <h2>{{ removal.item.item_number }} : {{ removal.source.removal_order_id }}</h2>
                        <p>
                            {{ date(removal) }}
                            <span v-if="removal.item">
                                {{ removal.item.name }}
                            </span>
                            <span v-else>
                                No item matched
                            </span>
                        </p>
                    </ion-label>
                </ion-item>
                <ion-item v-if="amazonRemovals.length < 1">
                    No amazon returns
                </ion-item>
            </ion-list>

        </ion-content>
    </ion-page>

</template>

<script>
import format from 'date-fns/format';
import _ from 'lodash';
import StandardHeader from "@/components/StandardHeader";
import {
    IonAvatar,
    IonButton,
    IonContent, IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonListHeader,
    IonPage,
    IonLoading,
    alertController, modalController
} from "@ionic/vue";
import {checkmarkCircle, search, barcode, listOutline} from 'ionicons/icons';
import {Capacitor} from "@capacitor/core";
import ItemSearchModalHelper from "@/services/ItemSearchModalHelper";
import ProductCardModal from "@/components/Modals/ProductCardModal";
import Process from "./Process";
export default {
    name: "FindItem",
    inject: ['$http', '$toast', '$validationAlert', '$scanner'],
    components: {
        IonAvatar,
        ProductCardModal,
        StandardHeader,
        IonLoading,
        IonContent,
        IonItem,
        IonLabel,
        IonList,
        IonListHeader,
        IonPage,
        IonIcon,
        IonButton
    },
    props: {
        type: String
    },
    computed: {
        customerReturns() {
            return _.filter(this.removals, ['is_amazon', false]);
        },
        amazonRemovals() {
            return _.filter(this.removals, ['is_amazon', true]);
        },
        include() {
            return 'skus,locations,locations.pivot,image_urls'
        },
        hasScannedItem() {
            return this.scannedItem && this.scannedItem.id > 0;
        }
    },
    methods: {
        date(removal) {
            return format(new Date(removal.created_at), 'dd/MM/yy');
        },
        customerReturn() {
            modalController.create({
                component: Process,
                componentProps: {
                    newReturnItem: this.scannedItem
                }
            }).then(m => {
                m.present();
                m.onDidDismiss().then(() => {
                    this.refresh(this.scannedItem);
                });
            });
        },
        open(removal) {
            modalController.create({
                component: Process,
                componentProps: {
                    itemReturn: removal
                }
            }).then(m => {
                m.present();
                m.onDidDismiss().then(() => {
                    this.refresh(this.scannedItem);
                });
            });
        },
        reset() {
            this.scannedItem = {};
            this.location = {};
        },
        refresh(item) {
            if (!item) {
                return;
            }

            this.busy = true;
            this.$http
                .get(`/api/items/${item.id}`, {params: {include: this.include}})
                .then(response => {
                    this.scannedItem = response.data;
                }).catch(error => {
                this.$validationAlert(error);
            }).finally(() => {
                this.busy = false;
            });

            this.$http
                .get(`/api/returns`, {params: {
                    include: 'item,source',
                    search: item.id, searchFields: 'item_id',
                    type: 'amazon'
                }})
                .then(response => {
                    this.removals = response.data.items;
                }).catch(error => {
                    this.$validationAlert(error);
                });
        },
        async openItemScanner() {
            if (Capacitor.platform === 'web') {
                const alert = await alertController.create({
                    header: 'Enter Item #ID',
                    inputs: [{
                        type: 'number',
                        name: 'item_id'
                    }],
                    buttons: ['Ok']
                });
                alert.onDidDismiss().then(input => {
                    const id = input.data.values.item_id;
                    this.$http.get(`/api/items/${id}`)
                        .then(response => {
                            this.refresh(response.data);
                        });
                });
                alert.present();
                return;
            }
            this.$scanner.search().then(result => {
                this.scannedItem = {};
                if (result) {
                    this.refresh(result);
                }
            }).catch(error => {
                this.scannedItem = {};
                this.$toast.danger(error);
            });
        },
        openItemPicker() {
            ItemSearchModalHelper.open(this.include).then(item => {
                if (! (item.data && item.data.id)) {
                    return;
                }
                this.refresh(item.data);
            });
        }
    },
    data() {
        return {
            busy: false,
            location: {},
            scannedItem: {},
            removals: []
        }
    },
    setup() {
        return {
            checkmarkCircle, search, barcode, listOutline, format
        }
    }

}
</script>
<style scoped>
ion-avatar {
    border-radius: 50%;
    background-color: var(--ion-color-primary);
    color: var(--ion-color-primary-contrast);
    font-weight: bold;
    display:flex;
}
</style>
